import type { ReactNode } from 'react';
import { AlertTriangle, Inbox } from 'lucide-react';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from './Button';
import { cx } from './cx';

export function ErrorBanner({ message, onRetry, className }: { message: ReactNode; onRetry?: () => void; className?: string }) {
  const { t } = useI18n();
  return (
    <div role="alert" className={cx('flex items-start gap-3 rounded-md border border-transparent bg-error-container px-4 py-3 text-body-sm text-error', className)}>
      <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">{message}</div>
      {onRetry ? (
        <button type="button" onClick={onRetry} className="font-semibold underline underline-offset-4 shrink-0">
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function NoticeBanner({ message, tone = 'info', className }: { message: ReactNode; tone?: 'info' | 'warning' | 'success'; className?: string }) {
  const tones = {
    info: 'bg-info-container text-info',
    warning: 'bg-warning-container text-warning',
    success: 'bg-success-container text-success',
  };
  return <div className={cx('rounded-md px-4 py-3 text-body-sm', tones[tone], className)}>{message}</div>;
}

export function EmptyState({
  title,
  message,
  icon,
  action,
  className,
}: {
  title?: ReactNode;
  message?: ReactNode;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  className?: string;
}) {
  const { t } = useI18n();
  return (
    <div className={cx('rounded-card border border-dashed border-hairline-strong bg-surface-lowest px-6 py-10 text-center', className)}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-surface-low text-ink-faint mb-3">
        {icon ?? <Inbox size={22} aria-hidden="true" />}
      </span>
      {title ? <p className="text-heading font-semibold text-ink">{title}</p> : null}
      <p className="text-body-sm text-ink-muted mt-1 max-w-md mx-auto">{message ?? t('common.empty.generic')}</p>
      {action ? (
        <Button variant="secondary" size="sm" className="mt-4" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton-block h-4 w-full', className)} aria-hidden="true" />;
}

/** Card-shaped skeleton for list loading. */
export function SkeletonRows({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cx('rounded-card border border-hairline bg-surface-lowest divide-y divide-hairline', className)} aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-4">
          <div className="skeleton-block h-10 w-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="skeleton-block h-3.5 w-1/2" />
            <div className="skeleton-block h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LoadingState({ label, className }: { label?: string; className?: string }) {
  const { t } = useI18n();
  return (
    <div className={cx('flex items-center justify-center gap-3 py-16 text-body-sm text-ink-muted', className)} role="status">
      <span className="h-5 w-5 rounded-full border-2 border-hairline-strong border-t-primary animate-spin" aria-hidden="true" />
      {label ?? t('common.loading')}
    </div>
  );
}
